import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import {
  FolderPlus,
  FolderOpen,
  ChevronRight,
  MoreHorizontal,
  Search,
  Loader2,
  Upload,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Progress } from '@/shared/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  useAllFolders,
  useBulkDelete,
  useBulkMove,
  useCreateFolder,
  useDeleteFile,
  useDeleteFolder,
  useFiles,
  useFolders,
  useRenameFolder,
  useUploadFiles,
} from '../hooks';
import type { FileFolder, ProjectFile } from '../types';
import { FileBrowser, type FileBrowserSelection } from './FileBrowser';
import { CreateFolderDialog } from './CreateFolderDialog';
import { FileUploadZone } from './FileUploadZone';
import { FileTree } from './FileTree';
import { BulkActionsBar } from './BulkActionsBar';
import { MoveDialog } from './MoveDialog';
import { FILES_TOUR_ID } from '@/features/tours/files';
import { useTour } from '@/features/tours';

interface UploadingFilePreview {
  id: string;
  name: string;
  progress: number;
}

interface MoveTarget {
  folderIds: string[];
  fileUuids: string[];
}

const EMPTY_SELECTION: FileBrowserSelection = { folderIds: [], fileUuids: [] };

export const FilesPage: React.FC = () => {
  const { folderId } = useParams<{ folderId?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const activeFolderId = folderId ?? null;

  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [uploadingPreviews, setUploadingPreviews] = useState<UploadingFilePreview[]>([]);
  const [selection, setSelection] = useState<FileBrowserSelection>(EMPTY_SELECTION);
  const [expandedTreeIds, setExpandedTreeIds] = useState<Set<string>>(new Set());
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const filters = useMemo(() => ({ search: search.trim() || undefined }), [search]);

  const { data: folders = [], isLoading: isLoadingFolders } = useFolders(
    numericProjectId,
    activeFolderId,
    filters,
  );
  const { data: files = [], isLoading: isLoadingFiles } = useFiles(
    numericProjectId,
    activeFolderId,
    filters,
  );
  const { data: allFolders = [], isLoading: isLoadingAllFolders } =
    useAllFolders(numericProjectId);

  const createFolderMutation = useCreateFolder(numericProjectId);
  const renameFolderMutation = useRenameFolder(numericProjectId);
  const deleteFolderMutation = useDeleteFolder(numericProjectId);
  const deleteFileMutation = useDeleteFile(numericProjectId);
  const uploadMutation = useUploadFiles(numericProjectId, activeFolderId);
  const bulkDeleteMutation = useBulkDelete(numericProjectId);
  const bulkMoveMutation = useBulkMove(numericProjectId);

  // Auto-expand the path to the active folder so the tree mirrors the URL.
  useEffect(() => {
    if (!activeFolderId) return;
    setExpandedTreeIds((prev) => {
      const next = new Set(prev);
      const byId = new Map(allFolders.map((folder) => [folder.id, folder]));
      let current: FileFolder | undefined = byId.get(activeFolderId);
      while (current?.parent) {
        next.add(current.parent);
        current = byId.get(current.parent);
      }
      return next;
    });
  }, [activeFolderId, allFolders]);

  // Drop any selection that points at rows that no longer exist after navigation.
  useEffect(() => {
    setSelection(EMPTY_SELECTION);
  }, [activeFolderId]);

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!numericProjectId) return;
    if (hasSeen(FILES_TOUR_ID)) return;
    void startTour(FILES_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, numericProjectId]);

  const isLoading = isLoadingFolders || isLoadingFiles;

  const goToFolder = (target: FileFolder | null) => {
    const basePath = location.pathname
      .replace(/\/folder\/[0-9a-fA-F-]+$/, '')
      .replace(/\/$/, '');
    if (!target) {
      navigate(basePath);
      return;
    }
    navigate(`${basePath}/folder/${target.id}`);
  };

  const breadcrumbs = useMemo(() => {
    const trail: FileFolder[] = [];
    if (!activeFolderId) return trail;
    const byId = new Map(allFolders.map((folder) => [folder.id, folder]));
    let current: FileFolder | undefined = byId.get(activeFolderId);
    while (current) {
      trail.unshift(current);
      current = current.parent ? byId.get(current.parent) : undefined;
    }
    return trail;
  }, [activeFolderId, allFolders]);

  const toggleTree = (id: string) =>
    setExpandedTreeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandTree = (id: string) =>
    setExpandedTreeIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  const handleDeleteFolder = async (folder: FileFolder) => {
    const confirmed = window.confirm(
      `Delete folder "${folder.name}" and everything inside it? This action cannot be undone.`,
    );
    if (!confirmed) return;
    await deleteFolderMutation.mutateAsync(folder.id);
  };

  const handleDeleteFile = async (file: ProjectFile) => {
    const confirmed = window.confirm(`Delete "${file.original_file_name}"?`);
    if (!confirmed) return;
    await deleteFileMutation.mutateAsync(file.uuid);
  };

  const handleBulkDelete = async () => {
    if (selection.folderIds.length + selection.fileUuids.length === 0) return;
    const summary = [
      selection.folderIds.length > 0
        ? `${selection.folderIds.length} folder${selection.folderIds.length === 1 ? '' : 's'}`
        : null,
      selection.fileUuids.length > 0
        ? `${selection.fileUuids.length} file${selection.fileUuids.length === 1 ? '' : 's'}`
        : null,
    ]
      .filter(Boolean)
      .join(' and ');
    const confirmed = window.confirm(
      `Delete ${summary}? Folders are deleted along with everything inside them.`,
    );
    if (!confirmed) return;
    await bulkDeleteMutation.mutateAsync({
      folderIds: selection.folderIds,
      fileUuids: selection.fileUuids,
    });
    setSelection(EMPTY_SELECTION);
  };

  const handleBulkDownload = () => {
    if (selection.fileUuids.length === 0) return;
    const selectedSet = new Set(selection.fileUuids);
    files
      .filter((file) => selectedSet.has(file.uuid) && file.file_url)
      .forEach((file, index) => {
        // Stagger downloads so the browser doesn't suppress them as a popup batch.
        setTimeout(() => {
          if (!file.file_url) return;
          const anchor = document.createElement('a');
          anchor.href = file.file_url;
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
          anchor.download = file.original_file_name;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }, index * 150);
      });
  };

  const handleUpload = async (incoming: File[]) => {
    const previews: UploadingFilePreview[] = incoming.map((file, index) => ({
      id: `${file.name}-${index}-${Date.now()}`,
      name: file.name,
      progress: 0,
    }));
    setUploadingPreviews(previews);

    try {
      await uploadMutation.mutateAsync({
        files: incoming,
        onFileProgress: (index, progressPct) => {
          setUploadingPreviews((prev) =>
            prev.map((item, itemIndex) =>
              itemIndex === index
                ? { ...item, progress: Math.min(100, Math.max(0, Math.round(progressPct))) }
                : item,
            ),
          );
        },
      });
    } finally {
      setTimeout(() => setUploadingPreviews([]), 500);
    }
  };

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-[100rem] space-y-4 p-4 sm:p-6">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <FolderOpen className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">Files</h1>
          </div>
          <div className="w-full sm:w-auto" data-tour-id="files-header-actions">
            <div className="hidden items-center gap-2 sm:flex">
              <Button
                variant="outline"
                onClick={() => setIsCreateOpen(true)}
                disabled={!numericProjectId}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                New Folder
              </Button>
              <Button
                onClick={() => setIsUploadOpen((prev) => !prev)}
                disabled={!numericProjectId}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="w-full sm:hidden" disabled={!numericProjectId}>
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={() => setIsCreateOpen(true)}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  New folder
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setIsUploadOpen((prev) => !prev)}>
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploadOpen ? 'Hide uploader' : 'Upload files'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Breadcrumbs trail={breadcrumbs} onNavigate={goToFolder} />

        {isUploadOpen && (
          <FileUploadZone
            disabled={!numericProjectId}
            isUploading={uploadMutation.isPending}
            onUpload={handleUpload}
          />
        )}

        {uploadingPreviews.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading {uploadingPreviews.length} file{uploadingPreviews.length === 1 ? '' : 's'}...
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {uploadingPreviews.map((upload) => (
                <Card key={upload.id} className="overflow-hidden">
                  <CardContent className="p-3 space-y-2">
                    <Skeleton className="w-full h-3 rounded-md" />
                    <p className="text-xs truncate" title={upload.name}>
                      {upload.name}
                    </p>
                    <Progress value={upload.progress} className="h-2" />
                    <p className="text-[11px] text-muted-foreground">{upload.progress}%</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          <aside
            className="rounded-md border bg-card p-2 md:sticky md:top-4 md:self-start md:max-h-[calc(var(--app-vh,1vh)*100-8rem)] md:overflow-y-auto"
            data-tour-id="files-tree"
          >
            <p className="px-2 pb-2 text-xs font-semibold uppercase text-muted-foreground tracking-wide">
              Folders
            </p>
            <FileTree
              folders={allFolders}
              activeFolderId={activeFolderId}
              expandedIds={expandedTreeIds}
              onToggle={toggleTree}
              onExpand={expandTree}
              onSelect={(id) => {
                const target = id ? allFolders.find((folder) => folder.id === id) : null;
                goToFolder(target ?? null);
              }}
              isLoading={isLoadingAllFolders}
            />
          </aside>

          <section className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-0 flex-1" data-tour-id="files-search">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search this folder by name"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>

            <BulkActionsBar
              selectedFolderCount={selection.folderIds.length}
              selectedFileCount={selection.fileUuids.length}
              onClear={() => setSelection(EMPTY_SELECTION)}
              onDelete={handleBulkDelete}
              onMove={() =>
                setMoveTarget({
                  folderIds: [...selection.folderIds],
                  fileUuids: [...selection.fileUuids],
                })
              }
              onDownload={handleBulkDownload}
              isBusy={bulkDeleteMutation.isPending || bulkMoveMutation.isPending}
            />

            <div data-tour-id="files-browser">
            <FileBrowser
              folders={folders}
              files={files}
              isLoading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              onOpenFolder={(folder) => goToFolder(folder)}
              onRenameFolder={async (folder, name) => {
                await renameFolderMutation.mutateAsync({ id: folder.id, name });
              }}
              onDeleteFolder={handleDeleteFolder}
              onDeleteFile={handleDeleteFile}
              onMoveFolder={(folder) =>
                setMoveTarget({ folderIds: [folder.id], fileUuids: [] })
              }
              onMoveFile={(file) =>
                setMoveTarget({ folderIds: [], fileUuids: [file.uuid] })
              }
            />
            </div>
          </section>
        </div>
      </div>

      <CreateFolderDialog
        open={isCreateOpen}
        isSubmitting={createFolderMutation.isPending}
        onClose={() => setIsCreateOpen(false)}
        onCreate={async (name) => {
          await createFolderMutation.mutateAsync({ parentId: activeFolderId, name });
        }}
      />

      {moveTarget && (
        <MoveDialog
          open={!!moveTarget}
          folders={allFolders}
          excludeFolderIds={moveTarget.folderIds}
          currentFolderId={activeFolderId}
          isSubmitting={bulkMoveMutation.isPending}
          onClose={() => setMoveTarget(null)}
          onMove={async (destination) => {
            await bulkMoveMutation.mutateAsync({
              folderIds: moveTarget.folderIds,
              fileUuids: moveTarget.fileUuids,
              destination,
            });
            setMoveTarget(null);
            setSelection(EMPTY_SELECTION);
          }}
        />
      )}
    </BaseLayout>
  );
};

interface BreadcrumbsProps {
  trail: FileFolder[];
  onNavigate: (folder: FileFolder | null) => void;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ trail, onNavigate }) => {
  return (
    <nav className="flex min-w-0 max-w-full flex-wrap items-center gap-1 overflow-hidden text-sm text-muted-foreground">
      <button
        type="button"
        onClick={() => onNavigate(null)}
        className={`shrink-0 transition-colors hover:text-foreground ${
          trail.length === 0 ? 'text-foreground font-medium' : ''
        }`}
      >
        All Files
      </button>
      {trail.map((folder, index) => {
        const isLast = index === trail.length - 1;
        return (
          <React.Fragment key={folder.id}>
            <ChevronRight className="h-4 w-4" />
            <button
              type="button"
              onClick={() => onNavigate(folder)}
              title={folder.name}
              className={`max-w-full truncate transition-colors hover:text-foreground ${
                isLast ? 'text-foreground font-medium' : ''
              }`}
            >
              {folder.name}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};
